import React from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import DecryptedImage from "../../../components/DecryptedImage";

const SurveyView = ({ survey }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "—";
    return value.toString();
  };

  if (!survey) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="ClipboardList" size={20} className="text-accent" />
          </div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            Survey Information
          </h2>
        </div>
        <div className="text-center py-12">
          <Icon
            name="ClipboardList"
            size={48}
            className="mx-auto mb-4 text-muted-foreground"
          />
          <p className="text-muted-foreground">
            No survey has been completed for this customer yet.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="ClipboardList" size={20} className="text-accent" />
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">
          Survey Information
        </h2>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="p-4 rounded-lg border border-border bg-muted/30">
            <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
              Roof Feasibility Status
            </label>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                survey.roof_feasible
                  ? "bg-success/10 text-success"
                  : "bg-error/10 text-error"
              }`}
            >
              {survey.roof_feasible === true
                ? "Feasible"
                : survey.roof_feasible === false
                  ? "Not Feasible"
                  : "-"}
            </span>
          </div>

          <div className="p-4 rounded-lg border border-border bg-muted/30">
            <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
              Submitted
            </label>
            <p className="text-base font-medium text-foreground">
              {formatDate(survey.submitted_at)}
            </p>
          </div>
        </div>

        {/* Rejection Reason */}
        {!survey.roof_feasible && survey.roof_rejection_reason && (
          <div className="p-4 rounded-lg border border-error/20 bg-error/5">
            <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
              Rejection Reason
            </label>
            <p className="text-base font-medium text-foreground">
              {survey.roof_rejection_reason
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </p>
          </div>
        )}

        {/* Meter Details */}
        {survey.proposed_capacity_kw && (
          <div className="p-4 rounded-lg border border-border bg-card">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Gauge" size={16} />
              Meter Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {survey.proposed_capacity_kw && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Proposed Rooftop Solar Capacity (kW)
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {formatNumber(survey.proposed_capacity_kw)} kW
                  </p>
                </div>
              )}
              {survey.sanctioned_load_kw && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Sanctioned Meter Load (kW)
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {formatNumber(survey.sanctioned_load_kw)} kW
                  </p>
                </div>
              )}
              {survey.phase_connection && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Phase Connection
                  </label>
                  <p className="text-sm font-medium text-foreground capitalize">
                    {survey.phase_connection}
                  </p>
                </div>
              )}
              {survey.lineman_name && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Lineman Name
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {survey.lineman_name}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Roof Engineering */}
        {(survey.roof_type ||
          survey.total_roof_area_sqft ||
          survey.roof_condition) && (
          <div className="p-4 rounded-lg border border-border bg-card">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Home" size={16} />
              Roof Engineering
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {survey.roof_type && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Roof Type
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {survey.roof_type}
                  </p>
                </div>
              )}
              {survey.total_roof_area_sqft && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Total Roof Area
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {formatNumber(survey.total_roof_area_sqft)} sq ft
                  </p>
                </div>
              )}
              {survey.shadow_free_area_sqft && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Shadow Free Area
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {formatNumber(survey.shadow_free_area_sqft)} sq ft
                  </p>
                </div>
              )}
              {survey.roof_condition && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Roof Condition
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {survey.roof_condition}
                  </p>
                </div>
              )}
              {survey.roof_orientation && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Roof Orientation
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {survey.roof_orientation}
                  </p>
                </div>
              )}
              {survey.parapet_height_ft && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Parapet Height
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {formatNumber(survey.parapet_height_ft)} ft
                  </p>
                </div>
              )}
              {survey.number_of_floors && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Number of Floors
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {formatNumber(survey.number_of_floors)}
                  </p>
                </div>
              )}
              {survey.access_type && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Access Type
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {survey.access_type}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Electrical & Site Logistics */}
        {(survey.meter_location ||
          survey.internet_status ||
          survey.earthing_soil_type) && (
          <div className="p-4 rounded-lg border border-border bg-card">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Zap" size={16} />
              Electrical & Site Logistics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {survey.meter_location && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Meter Location
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {survey.meter_location}
                  </p>
                </div>
              )}
              {survey.internet_status && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Internet Status
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {survey.internet_status}
                  </p>
                </div>
              )}
              {survey.earthing_soil_type && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
                    Earthing Soil Type
                  </label>
                  <p className="text-sm font-medium text-foreground">
                    {survey.earthing_soil_type}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Obstacles */}
        {(survey.no_obstacle ||
          survey.water_tank_present ||
          survey.neighbors_wall_present ||
          survey.trees_present) && (
          <div className="p-4 rounded-lg border border-border bg-card">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="AlertTriangle" size={16} />
              Obstacles
            </h3>
            <div className="flex flex-wrap gap-4">
              {survey.no_obstacle && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-success/10 text-success">
                  No Obstacle
                </span>
              )}
              {survey.water_tank_present && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-warning/10 text-warning">
                  Water Tank Present
                </span>
              )}
              {survey.neighbors_wall_present && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-warning/10 text-warning">
                  Neighbor's Wall Present
                </span>
              )}
              {survey.trees_present && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-warning/10 text-warning">
                  Trees Present
                </span>
              )}
            </div>
          </div>
        )}

        {/* Roof Inspection Photos (from Roof Inspection step) */}
        {survey.roof_inspection_photos?.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Camera" size={16} />
              Roof Inspection Photos
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {survey.roof_inspection_photos.map((photoUrl, index) => (
                <div
                  key={index}
                  className="relative rounded-lg overflow-hidden border border-border group"
                >
                  <Image
                    src={photoUrl}
                    alt={`Roof inspection photo ${index + 1}`}
                    className="w-full h-48 object-contain bg-muted"
                  />
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    Photo {index + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categorized Photos (Site Photos) */}
        {(survey.house_front_view_photos?.length > 0 ||
          survey.meter_photos?.length > 0 ||
          survey.line_diagram_photos?.length > 0 ||
          survey.obstacle_photos?.length > 0) && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Camera" size={16} />
              Site Photos
            </h3>
            <div className="space-y-6">
              {/* House Front View Photos */}
              {survey.house_front_view_photos?.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-3 block">
                    House Front View
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {survey.house_front_view_photos.map((photoUrl, index) => (
                      <div
                        key={index}
                        className="relative rounded-lg overflow-hidden border border-border group"
                      >
                        <Image
                          src={photoUrl}
                          alt={`Roof view photo ${index + 1}`}
                          className="w-full h-48 object-contain bg-muted"
                        />
                        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          Photo {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Meter Photos */}
              {survey.meter_photos?.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-3 block">
                    Meter Photos
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {survey.meter_photos.map((photoUrl, index) => (
                      <div
                        key={index}
                        className="relative rounded-lg overflow-hidden border border-border group"
                      >
                        <Image
                          src={photoUrl}
                          alt={`Meter photo ${index + 1}`}
                          className="w-full h-48 object-contain bg-muted"
                        />
                        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          Photo {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Line Diagram Photos */}
              {survey.line_diagram_photos?.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-3 block">
                    Line Diagram
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {survey.line_diagram_photos.map((photoUrl, index) => (
                      <div
                        key={index}
                        className="relative rounded-lg overflow-hidden border border-border group"
                      >
                        <Image
                          src={photoUrl}
                          alt={`Line diagram ${index + 1}`}
                          className="w-full h-48 object-contain bg-muted"
                        />
                        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          Photo {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Obstacle Photos */}
              {survey.obstacle_photos?.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-3 block">
                    Obstacle Photos
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {survey.obstacle_photos.map((photoUrl, index) => (
                      <div
                        key={index}
                        className="relative rounded-lg overflow-hidden border border-border group"
                      >
                        <Image
                          src={photoUrl}
                          alt={`Obstacle photo ${index + 1}`}
                          className="w-full h-48 object-contain bg-muted"
                        />
                        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          Photo {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Document Photos */}
        {(survey.aadhar_photo ||
          survey.bank_passbook_photo ||
          survey.electricity_bill_photo) && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="FileText" size={16} />
              Document Photos
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {survey.aadhar_photo && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Aadhar Card
                  </label>
                  <div className="rounded-lg overflow-hidden border border-border">
                    <DecryptedImage
                      src={survey.aadhar_photo}
                      alt="Aadhar Card"
                      className="w-full h-64 object-contain bg-muted"
                    />
                  </div>
                </div>
              )}
              {survey.bank_passbook_photo && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Bank Passbook
                  </label>
                  <div className="rounded-lg overflow-hidden border border-border">
                    <DecryptedImage
                      src={survey.bank_passbook_photo}
                      alt="Bank Passbook"
                      className="w-full h-64 object-contain bg-muted"
                    />
                  </div>
                </div>
              )}
              {survey.electricity_bill_photo && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Electricity Bill
                  </label>
                  <div className="rounded-lg overflow-hidden border border-border">
                    <DecryptedImage
                      src={survey.electricity_bill_photo}
                      alt="Electricity Bill"
                      className="w-full h-64 object-contain bg-muted"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tentative location photos */}
        {(survey.tentative_panel_location_photo ||
          survey.tentative_inverter_location_photo) && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="MapPin" size={16} />
              Tentative Locations
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {survey.tentative_panel_location_photo && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Panel Location Photo
                  </label>
                  <div className="rounded-lg overflow-hidden border border-border">
                    <Image
                      src={survey.tentative_panel_location_photo}
                      alt="Tentative panel location"
                      className="w-full h-64 object-contain bg-muted"
                    />
                  </div>
                </div>
              )}
              {survey.tentative_inverter_location_photo && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Inverter Location Photo
                  </label>
                  <div className="rounded-lg overflow-hidden border border-border">
                    <Image
                      src={survey.tentative_inverter_location_photo}
                      alt="Tentative inverter location"
                      className="w-full h-64 object-contain bg-muted"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Signatures */}
        {(survey.surveyor_signature_photo ||
          survey.customer_signature_photo) && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="PenTool" size={16} />
              Signatures
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {survey.surveyor_signature_photo && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Surveyor Signature
                  </label>
                  <div className="rounded-lg overflow-hidden border border-border bg-white">
                    <Image
                      src={survey.surveyor_signature_photo}
                      alt="Surveyor signature"
                      className="w-full h-48 object-contain"
                    />
                  </div>
                </div>
              )}
              {survey.customer_signature_photo && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Customer Signature
                  </label>
                  <div className="rounded-lg overflow-hidden border border-border bg-white">
                    <Image
                      src={survey.customer_signature_photo}
                      alt="Customer signature"
                      className="w-full h-48 object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Consent */}
        {survey.consent_accepted !== null &&
          survey.consent_accepted !== undefined && (
            <div className="p-4 rounded-lg border border-border bg-card">
              <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
                Consent
              </label>
              <p className="text-sm text-foreground">
                {survey.consent_accepted ? "Accepted" : "Not accepted"}
              </p>
            </div>
          )}

        {/* QR Code */}
        {survey.qr_code_photo && (
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              QR Code Sticker
            </label>
            <div className="rounded-lg overflow-hidden border border-border bg-muted max-w-md">
              <div className="aspect-square flex items-center justify-center">
                <Image
                  src={survey.qr_code_photo}
                  alt="QR Code Sticker"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        )}

        {/* Survey Notes */}
        {survey.survey_notes && (
          <div className="p-4 rounded-lg border border-border bg-card">
            <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
              Survey Notes
            </label>
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {survey.survey_notes}
            </p>
          </div>
        )}

        {/* Sign-offs */}
        {(survey.surveyor_signed || survey.customer_signed) && (
          <div className="p-4 rounded-lg border border-border bg-card">
            <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
              Sign-offs
            </label>
            <div className="flex gap-4">
              {survey.surveyor_signed && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-success/10 text-success">
                  <Icon name="CheckCircle2" size={14} className="mr-1" />
                  Surveyor Signed
                </span>
              )}
              {survey.customer_signed && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-success/10 text-success">
                  <Icon name="CheckCircle2" size={14} className="mr-1" />
                  Customer Signed
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyView;
